import type { StorageAdapter, StorageService } from './types';

type NamespaceClearableAdapter = StorageAdapter & {
  removeKeysMatching(predicate: (key: string) => boolean): void;
};

function supportsNamespaceClear(
  adapter: StorageAdapter
): adapter is NamespaceClearableAdapter {
  return (
    'removeKeysMatching' in adapter &&
    typeof adapter.removeKeysMatching === 'function'
  );
}

export class StorageServiceImpl implements StorageService {
  private readonly adapter: StorageAdapter;

  private readonly namespace: string;

  private readonly prefix?: string;

  constructor(adapter: StorageAdapter, namespace: string, prefix?: string) {
    this.adapter = adapter;
    this.namespace = namespace;
    this.prefix = prefix;
  }

  get<T>(key: string): T | null {
    const raw = this.adapter.get(this.createKey(key));
    if (raw === null) return null;

    try {
      return JSON.parse(raw) as T;
    } catch {
      return null;
    }
  }

  set<T>(key: string, value: T): void {
    this.adapter.set(this.createKey(key), JSON.stringify(value));
  }

  remove(key: string): void {
    this.adapter.remove(this.createKey(key));
  }

  has(key: string): boolean {
    return this.adapter.get(this.createKey(key)) !== null;
  }

  clear(): void {
    const namespaceKeyPrefix = this.getNamespaceKeyPrefix();

    if (supportsNamespaceClear(this.adapter)) {
      this.adapter.removeKeysMatching(key =>
        key.startsWith(namespaceKeyPrefix)
      );
      return;
    }

    this.adapter.clear();
  }

  createKey(key: string): string {
    if (this.prefix) {
      return `${this.prefix}:${this.namespace}:${key}`;
    }

    return `${this.namespace}:${key}`;
  }

  private getNamespaceKeyPrefix(): string {
    if (this.prefix) {
      return `${this.prefix}:${this.namespace}:`;
    }

    return `${this.namespace}:`;
  }
}
